import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTabListState } from '@react-stately/tabs';
import { useTabList } from '@react-aria/tabs';
import useDimensions from 'react-cool-dimensions';
import { Button, Col, Row } from '@geberit/gdds';

// components
import { Tab } from './tab';
import { TabPanel } from './tab-panel';

// types
import { TabListProps } from '@react-types/tabs';

// styles
import styles from './sliding-tabs.module.scss';

// utils
import { classNameBuilder } from '../../../utils/classNameBuilder';
import { useIsMobile, useIsTablet } from 'components/App/SizeProvider';

function Tabs({ ...props }: Readonly<TabListProps<Tab>>) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const state = useTabListState(props);
  const ref = useRef<HTMLDivElement>(null);

  const id = React.useId();
  const { tabListProps } = useTabList(props, state, ref);
  tabListProps.id = `react-aria${id}`;

  const [scrollX, setScrollX] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [offsetWidth, setOffsetWidth] = useState(0);

  const { observe } = useDimensions({
    onResize: ({ entry }) => {
      setScrollWidth(entry.target.scrollWidth);
      setOffsetWidth(entry.target.clientWidth);
    },
  });

  const showLeftButton = scrollX > 0;
  const showRightButton = scrollX + offsetWidth + 20 < scrollWidth;

  function getButtonOffset() {
    if (isMobile) {
      return 24;
    }

    if (isTablet) {
      return 32;
    }

    return 44;
  }
  const buttonOffset = useMemo(getButtonOffset, [isMobile, isTablet]);

  useEffect(() => {
    if (showLeftButton) {
      setScrollX(scrollX + buttonOffset);
    }
  }, [showLeftButton]);

  useEffect(() => {
    const track = ref.current?.querySelector<HTMLDivElement>('.track');
    track?.scroll?.({ left: scrollX, behavior: 'auto' });
  }, [scrollX]);

  function renderRightButton(): ReactNode {
    if (!showRightButton) return null;

    return (
      <div className={classNameBuilder(styles.buttonWrapper, styles.right)}>
        <Button
          onClick={() => {
            if (scrollX + offsetWidth > scrollWidth) {
              setScrollX(scrollWidth - (scrollX + offsetWidth));
            } else {
              setScrollX((x) => x + 200);
            }
          }}
          stylingType="icon"
          symbol="chevron_right"
        ></Button>
      </div>
    );
  }

  function renderLeftButton(): ReactNode {
    if (!showLeftButton) return null;
    return (
      <div className={classNameBuilder(styles.buttonWrapper, styles.left)}>
        <Button
          onClick={() => {
            if (scrollX > 0) setScrollX((x) => x - 200);
          }}
          stylingType="icon"
          symbol="chevron_left"
        ></Button>
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col size={[0, 0, 2]} />
        <Col size={[4, 8, 8]}>
          <div className={styles.wrapper} {...tabListProps} ref={ref}>
            {renderLeftButton()}
            <div
              ref={observe}
              className={classNameBuilder(styles.track, 'track')}
              style={{
                marginRight: showRightButton ? buttonOffset : 0,
                marginLeft: showLeftButton ? buttonOffset : 0,
              }}
            >
              {Array.from(state.collection).map((item, index) => (
                <Tab
                  onScroll={setScrollX}
                  key={item.key}
                  item={item}
                  state={state}
                  tabIndex={index}
                />
              ))}
            </div>
            {renderRightButton()}
          </div>
        </Col>
        <Col size={[0, 0, 2]} />
      </Row>
      <Row>
        <Col>
          <TabPanel key={state.selectedItem?.key} state={state} />
        </Col>
      </Row>
    </div>
  );
}

export default Tabs;
